import {
  DetailedRequisitionSerializer,
  NewRequisitionForm,
} from "../../recoil/types";
import { getInitialNewRequisitionForm } from "../../recoil/helpers";
import { formatOrderingProvider } from "./Services/helpers";
import {
  RequisitionValidationErrorKey,
  getEditOrderValidationErrors,
} from "./helpers";

export type ReportUpdatePayload = Partial<{
  first_name: string;
  last_name: string;
  medical_record_number: string;
  date_of_birth: string;
  due_date: string;
  gestation: "singleton" | "twin" | "triplet_or_more";
  egg_donor: "egg_donor" | "not_egg_donor";
  lims_id: string;
  npi: string;
}>;

const ETHNICITY_FIELDS: Record<string, keyof NewRequisitionForm> = {
  Asian: "asian",
  "African or African American": "african",
  "Ashkenazi Jewish": "ashkenazi",
  "Middle Eastern": "middleEastern",
  "French Canadian or Cajun": "frenchCanadian",
  Hispanic: "hispanic",
  "Northern European (Caucasian)": "white",
  White: "white",
  Other: "otherEthnicity",
  Unknown: "unknownEthnicity",
};

// Salesforce stores a multi-select picklist; portal orders join with commas.
const toEthnicityFields = (
  ethnicity: string | null | undefined,
): Partial<NewRequisitionForm> =>
  Object.fromEntries(
    (ethnicity ?? "")
      .split(/[;,]/)
      .map((label) => ETHNICITY_FIELDS[label.trim()])
      .filter(Boolean)
      .map((field) => [field, true]),
  );

export const convertRequisitionToReportUpdateForm = (
  requisition: DetailedRequisitionSerializer,
): NewRequisitionForm => {
  const physicianName = requisition.physician_name;
  const npi = requisition.npi ?? "";
  return {
    // The order's test flags stay unset, so clinic choices are only checked
    // for region, matching the server.
    ...getInitialNewRequisitionForm(),
    clinicAccountNumber: requisition.lims_id,
    clinicName: requisition.account_name,
    clinicPhone: requisition.account_phone,
    clinicFax: requisition.account_fax,
    orderingHealthcareProviders: physicianName,
    orderingHealthcareNPI: npi,
    orderingHealthcareProviderFormatted: physicianName
      ? formatOrderingProvider(physicianName, npi)
      : "",
    firstName: requisition.first_name ?? "",
    lastName: requisition.last_name ?? "",
    medicalRecordNumber: requisition.medical_record_number ?? "",
    dateOfBirth: requisition.date_of_birth ?? "",
    estimatedDueDate: requisition.due_date ?? "",
    collectionDate: requisition.collection_date ?? "",
    sex: requisition.gender === "Male" ? "Male" : "Female",
    notPregnant: requisition.is_pregnant === false,
    isSingleton: requisition.gestation === "singleton",
    isTwin: requisition.gestation === "twin",
    isTripleOrMore: requisition.gestation === "triplet_or_more",
    isEggDonor: requisition.egg_donor === "egg_donor",
    aneuploidyNIPT: requisition.is_aneuploidy_included,
    cellPhone: requisition.phone_number ?? "",
    emailAddress: requisition.email ?? "",
    streetAddress: requisition.street_address_1 ?? "",
    aptUnit: requisition.street_address_2 ?? "",
    city: requisition.city ?? "",
    state: requisition.state ?? "",
    zipCode: requisition.zip ?? "",
    ...toEthnicityFields(requisition.ethnicity),
  };
};

const toGestation = (form: NewRequisitionForm) => {
  if (form.isTripleOrMore) return "triplet_or_more";
  if (form.isTwin) return "twin";
  return form.isSingleton ? "singleton" : undefined;
};

export const buildReportUpdatePayload = (
  form: NewRequisitionForm,
  originalForm: NewRequisitionForm,
): ReportUpdatePayload => {
  const changed = (field: keyof NewRequisitionForm) =>
    form[field] !== originalForm[field];
  const gestation = toGestation(form);
  return {
    ...(changed("firstName") && { first_name: form.firstName }),
    ...(changed("lastName") && { last_name: form.lastName }),
    ...(changed("medicalRecordNumber") && {
      medical_record_number: form.medicalRecordNumber,
    }),
    ...(changed("dateOfBirth") && { date_of_birth: form.dateOfBirth }),
    ...(changed("estimatedDueDate") && { due_date: form.estimatedDueDate }),
    ...(gestation && gestation !== toGestation(originalForm) && { gestation }),
    ...(changed("isEggDonor") && {
      egg_donor: form.isEggDonor ? "egg_donor" : "not_egg_donor",
    }),
    // A clinic change clears the provider, so both are sent together.
    ...((changed("clinicAccountNumber") ||
      changed("orderingHealthcareNPI")) && {
      lims_id: form.clinicAccountNumber,
      npi: form.orderingHealthcareNPI,
    }),
  };
};

const REPORT_UPDATE_VALIDATION_KEYS: Record<
  keyof ReportUpdatePayload,
  RequisitionValidationErrorKey[]
> = {
  first_name: ["patient.firstName"],
  last_name: ["patient.lastName"],
  medical_record_number: [],
  date_of_birth: ["patient.dateOfBirth"],
  due_date: ["patient.estimatedDueDate"],
  gestation: [],
  egg_donor: [],
  lims_id: ["clinic.accountNumber", "clinic.name"],
  npi: ["clinic.provider", "clinic.providerNpi"],
};

// Completed orders can hold incomplete or historical data, so only the
// fields being corrected are validated.
export const hasReportUpdateValidationError = (
  form: NewRequisitionForm,
  originalForm: NewRequisitionForm,
): boolean => {
  const errors = getEditOrderValidationErrors(form);
  const fields = Object.keys(
    buildReportUpdatePayload(form, originalForm),
  ) as (keyof ReportUpdatePayload)[];
  return fields.some((field) =>
    REPORT_UPDATE_VALIDATION_KEYS[field].some((key) => errors[key]),
  );
};
